import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import packageJson from '../../package.json';
import {
  buildLendAPIIframeSrc,
  getValidSessionFromURL,
  isProdTesting,
  isE2EProdTesting,
} from '../utils/lendAPIUtils';
import LendAPIIframe from '../shared/components/LendAPIIframe';
import PreQualFunnelLayout from '../prequal/PreQualFunnelLayout';
import LendAPIFunnelHeaderRight from '../shared/components/LendAPIFunnelHeaderRight';
import useLendAPILogin from '../hooks/useLendAPILogin';
import { useAuth } from '../hooks/useAuth';
import { useFirestore } from '../hooks/useFirestore';
import { useUser } from '../hooks/useUser';
import { LendAPIPayload } from '../utils/lendAPIPrequalHelper';
import {
  LendApiFullAppTab,
  LEND_API_ORIGIN,
  KAMELEOON_FULLAPP_FUNNEL_LOADED,
  BOAT_TRADER_SOURCE_ID,
  LENDAPI_APPLICATION_HEADER_VALUE_PROPS,
  isProduction,
} from '../constants';
import { shouldShowProgressBar } from '../prequal/prequalProgressConfig';
import { FULLAPP_PROGRESS_CONFIG } from './fullAppProgressConfig';
import {
  mapLendAPIToFullAppStarted,
  mapLendAPIToFullAppCompleted,
} from '../utils/lendAPIIterableHelper';
import { sendCustomEventToIterable } from '../utils/iterable';
import { recordFinanceIdentity } from '../utils/recordFinanceIdentity';
import { validateLendAPIRoute, normalizeProductType } from '../utils/lendAPIRouteValidation';
import PageNotFound from './components/PageNotFound';
import {
  resolveProductHooks,
  getProductConfigById,
  processPerformanceTracking,
} from '../utils/lendAPIConfigHelper';
import { isTabMatch } from '../utils/lendAPIGAHelper';
import { sendKameleoonGoalConversion } from '../utils/lendAPIKameleoonHelper';
import { LendAPIProductHooks } from '../lendAPIConfig.types';
import { captureFullQueryString } from '../utils/queryParamHelper';
import { buildResumeLink } from '../utils/buildResumeLink';
import {
  extractBoatInfoAndSourceFromPayload,
  EnrichBoatInfoInput,
} from '../utils/lendAPIEnrichmentHelper';
import { extractImtIdFromBoatUrl } from '../utils/collateralUrlHelper';

const DYNAMIC_APP_INTERNAL_VERSION =
  process.env.REACT_APP_INTERNAL_VERSION || packageJson.version || '1.0.0';

interface RouteParams {
  productType: string;
  productId: string;
  productSlug: string;
}

const getE2EProdTesting = (): boolean => isE2EProdTesting(window.location.search);

const getPrequalIdFromLocalStorage = (): string | undefined => {
  try {
    const imtId = localStorage.getItem('current-lendapi-prequal-imtid');
    if (imtId) {
      const key = `prequal-${imtId}`;
      const data = localStorage.getItem(key);
      if (data) {
        const parsed = JSON.parse(data);
        return parsed.prequalApplicationId;
      }
    }
  } catch (error) {
    console.error('[LendAPI] Error retrieving prequalId from localStorage:', error);
  }
  return undefined;
};

const processFullAppContactInfoHook = async (payload: LendAPIPayload): Promise<void> => {
  const applicationId = payload.session;
  const prequalId = getPrequalIdFromLocalStorage();
  const email = payload.email || new URLSearchParams(window.location.search).get('email') || '';

  if (applicationId && email) {
    try {
      const resumeLink = buildResumeLink(window.location.href, applicationId, email);
      const enrichedPayload = { ...payload, email };
      const eventData = mapLendAPIToFullAppStarted(
        enrichedPayload,
        applicationId,
        prequalId,
        resumeLink
      );
      const e2eProdTesting = getE2EProdTesting();
      await sendCustomEventToIterable({ ...eventData, e2eProdTesting });
      console.log('[LendAPI FullApp][Iterable] FullAppStarted event sent successfully');
    } catch (error) {
      console.error('[LendAPI FullApp][Iterable] Error sending FullAppStarted event:', error);
    }
  } else {
    console.warn(
      '[LendAPI FullApp][Iterable][processFullAppContactInfoHook] Skipping event - missing required data:',
      {
        hasApplicationId: !!applicationId,
        hasEmail: !!email,
      }
    );
  }
};

const processFullAppCompleteHook = async (
  payload: LendAPIPayload,
  identityRecordedRef: React.MutableRefObject<string | null>
): Promise<void> => {
  const applicationId = payload.session;
  const prequalId = getPrequalIdFromLocalStorage();
  const email = payload.email || new URLSearchParams(window.location.search).get('email') || '';

  if (applicationId && identityRecordedRef.current !== applicationId) {
    identityRecordedRef.current = applicationId;
    recordFinanceIdentity('application');
  }

  if (applicationId && email) {
    try {
      const enrichedPayload = { ...payload, email };
      const eventData = mapLendAPIToFullAppCompleted(enrichedPayload, applicationId, prequalId);
      const e2eProdTesting = getE2EProdTesting();
      await sendCustomEventToIterable({ ...eventData, e2eProdTesting });
      console.log('[LendAPI FullApp][Iterable] FullAppCompleted event sent successfully');
    } catch (error) {
      console.error('[LendAPI FullApp][Iterable] Error sending FullAppCompleted event:', error);
    }
  } else {
    console.warn(
      '[LendAPI FullApp][Iterable][processFullAppCompleteHook] Skipping event - missing required data:',
      {
        hasApplicationId: !!applicationId,
        hasEmail: !!email,
      }
    );
  }
};

interface CallEnrichLendAPIApplicationArgs {
  applicationId: string;
  boatInfo: EnrichBoatInfoInput;
  productType: string;
  source: number;
}

// TRIDENT-971: BoatTrader-sourced-only, best-effort enrichment of the LendAPI
// application with normalized boat/engine identity for the JD Power boat-details
// prefill. Deliberately NOT routed through processFullAppContactInfoHook — that
// hook stays Iterable-only.
const processEnrichLendAPIApplicationHook = async (
  payload: LendAPIPayload,
  productType: string,
  callEnrichLendAPIApplication: (args: CallEnrichLendAPIApplicationArgs) => Promise<void>
): Promise<void> => {
  const { boatInfo, source } = extractBoatInfoAndSourceFromPayload(payload);

  if (source !== BOAT_TRADER_SOURCE_ID || !boatInfo) {
    console.log(
      '[LendAPI FullApp][Enrichment] Skipping - not a BoatTrader-sourced boatInfo payload',
      {
        hasBoatInfo: !!boatInfo,
        source,
      }
    );
    return;
  }

  const applicationId = payload.session;
  if (!applicationId) {
    console.log('[LendAPI FullApp][Enrichment] Skipping - no session/applicationId on payload');
    return;
  }

  await callEnrichLendAPIApplication({ applicationId, boatInfo, productType, source });
};

interface CallProcessCollateralURLArgs {
  applicationId: string;
  boatUrl: string;
  productType: string;
  source: number | null;
  imtId?: string;
}

// TRIDENT-999: fires on the Loan Info tab's formSubmission whenever a BoatTrader
// listing URL (boat_url) is present. Always calls through with boatUrl so the
// backend can persist it unconditionally (Salesforce Collateral URL mapping);
// the imtId argument is included only when it's newly-known (absent or
// different from the click-through's own imtId decoded from the SAME payload) —
// this is what lets the backend skip a redundant listing lookup when the user's
// typed/prefilled URL resolves to the same boat they already clicked through on.
const processCollateralURLHook = async (
  payload: LendAPIPayload,
  productType: string,
  callProcessCollateralURL: (args: CallProcessCollateralURLArgs) => Promise<void>
): Promise<void> => {
  const boatUrl = typeof payload.boat_url === 'string' ? payload.boat_url : undefined;
  if (!boatUrl) {
    return;
  }

  const applicationId = payload.session;
  if (!applicationId) {
    console.log('[processCollateralURL] Skipping - no session/applicationId on payload');
    return;
  }

  const { boatInfo: clickThroughBoatInfo, source } = extractBoatInfoAndSourceFromPayload(payload);
  const clickThroughImtId = (clickThroughBoatInfo as { imtId?: number | string } | null)?.imtId;
  const extractedImtId = extractImtIdFromBoatUrl(boatUrl);

  if (!extractedImtId) {
    console.log('[processCollateralURL] no-op: off-domain/no IMT id — persisting boatUrl only', {
      boatUrl,
    });
  }

  const imtIdMatchesClickThrough =
    !!extractedImtId &&
    clickThroughImtId !== undefined &&
    clickThroughImtId !== null &&
    String(clickThroughImtId) === extractedImtId;

  await callProcessCollateralURL({
    applicationId,
    boatUrl,
    productType,
    source,
    ...(extractedImtId && !imtIdMatchesClickThrough && { imtId: extractedImtId }),
  });
};

const processHooks = async (
  eventData: any,
  eventType: string | undefined,
  tabName: string | undefined,
  hooks: LendAPIProductHooks | undefined,
  productType: string,
  callEnrichLendAPIApplication: (args: CallEnrichLendAPIApplicationArgs) => Promise<void>,
  callProcessCollateralURL: (args: CallProcessCollateralURLArgs) => Promise<void>,
  identityRecordedRef: React.MutableRefObject<string | null>
): Promise<void> => {
  if (!eventData || !eventData.payload || !hooks) {
    return;
  }

  // Each branch below is independently isolated: a throw in one hook must
  // never prevent a sibling hook sharing the same trigger tab from running
  // (e.g. the best-effort enrichLendAPIApplication branch must still fire
  // even if the unrelated Iterable contact-info hook throws first).
  if (
    hooks.fullAppContactInfo &&
    eventType === hooks.fullAppContactInfo.type &&
    isTabMatch(tabName || '', hooks.fullAppContactInfo.triggerTab)
  ) {
    try {
      await processFullAppContactInfoHook(eventData.payload as LendAPIPayload);
    } catch (error) {
      console.error('[LendAPI FullApp][processHooks] fullAppContactInfo hook failed:', error);
    }
  }

  if (
    hooks.fullAppComplete &&
    eventType === hooks.fullAppComplete.type &&
    isTabMatch(tabName || '', hooks.fullAppComplete.triggerTab)
  ) {
    try {
      await processFullAppCompleteHook(eventData.payload as LendAPIPayload, identityRecordedRef);
    } catch (error) {
      console.error('[LendAPI FullApp][processHooks] fullAppComplete hook failed:', error);
    }
  }

  if (
    hooks.enrichLendAPIApplication &&
    hooks.enrichLendAPIApplication.enabled !== false &&
    eventType === hooks.enrichLendAPIApplication.type &&
    isTabMatch(tabName || '', hooks.enrichLendAPIApplication.triggerTab)
  ) {
    try {
      await processEnrichLendAPIApplicationHook(
        eventData.payload as LendAPIPayload,
        productType,
        callEnrichLendAPIApplication
      );
    } catch (error) {
      console.error('[LendAPI FullApp][processHooks] enrichLendAPIApplication hook failed:', error);
    }
  }

  if (
    hooks.processCollateralURL &&
    hooks.processCollateralURL.enabled !== false &&
    eventType === hooks.processCollateralURL.type &&
    isTabMatch(tabName || '', hooks.processCollateralURL.triggerTab)
  ) {
    try {
      await processCollateralURLHook(
        eventData.payload as LendAPIPayload,
        productType,
        callProcessCollateralURL
      );
    } catch (error) {
      console.error('[LendAPI FullApp][processHooks] processCollateralURL hook failed:', error);
    }
  }
};

const LendAPIFullAppFunnel: React.FC = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { productType: urlProductType, productId, productSlug } = useParams<RouteParams>();
  const [currentTab, setCurrentTab] = useState<LendApiFullAppTab | string>(
    LendApiFullAppTab.LETS_GET_STARTED
  );
  const [lastKnownTab, setLastKnownTab] = useState<LendApiFullAppTab | string>(
    LendApiFullAppTab.LETS_GET_STARTED
  );

  const funnelLoadedFired = useRef(false);
  const enrichFiredRef = useRef(false);
  // TRIDENT-999: dedicated ref guard for the new processCollateralURL hook,
  // deliberately SEPARATE from enrichFiredRef — that ref is scoped to the
  // click-through boatInfo path (PrimaryProfile/AboutYou-Individual) and must
  // never block this hook from firing on the Loan Info tab, and vice versa.
  const collateralUrlFiredRef = useRef(false);
  const identityRecordedRef = useRef<string | null>(null);

  const productConfig = useMemo(() => getProductConfigById(productId), [productId]);
  const productType = useMemo(() => normalizeProductType(urlProductType || ''), [urlProductType]);

  const auth = useAuth();
  const firestore = useFirestore();
  const user = useUser();
  const isAuthenticated = !!user && !user.isAnonymous;
  const { hasSession, handleLoginSuccess, captureSessionFromPayload } = useLendAPILogin({
    auth,
    firestore,
  });

  // TRIDENT-971: best-effort, once-per-session invocation of the enrichLendAPIApplication
  // callable. The ref guard is checked/set BEFORE the async call so overlapping repeat
  // formSubmission events on the same tab can never race into a double-PUT.
  const callEnrichLendAPIApplication = useCallback(
    async ({
      applicationId,
      boatInfo,
      productType: callProductType,
      source,
    }: CallEnrichLendAPIApplicationArgs) => {
      if (enrichFiredRef.current) {
        return;
      }
      enrichFiredRef.current = true;

      try {
        const prodTesting = isProdTesting(captureFullQueryString());
        const enrichLendAPIApplication = firebase
          .functions()
          .httpsCallable('enrichLendAPIApplication');
        await enrichLendAPIApplication({
          applicationId,
          boatInfo,
          productType: callProductType,
          source,
          prodTesting,
        });
        console.log('[LendAPI FullApp][Enrichment] enrichLendAPIApplication callable invoked');
      } catch (error) {
        console.warn(
          '[LendAPI FullApp][Enrichment] enrichLendAPIApplication callable failed:',
          error
        );
      }
    },
    []
  );

  // TRIDENT-999: best-effort, once-per-session invocation of the same
  // enrichLendAPIApplication callable, but from the Loan Info tab's own
  // boat_url. Uses collateralUrlFiredRef (NOT enrichFiredRef) so it fires
  // independently of the click-through hook above.
  const callProcessCollateralURL = useCallback(
    async ({
      applicationId,
      boatUrl,
      productType: callProductType,
      source,
      imtId,
    }: CallProcessCollateralURLArgs) => {
      if (collateralUrlFiredRef.current) {
        return;
      }
      collateralUrlFiredRef.current = true;

      try {
        const prodTesting = isProdTesting(captureFullQueryString());
        const enrichLendAPIApplication = firebase
          .functions()
          .httpsCallable('enrichLendAPIApplication');
        await enrichLendAPIApplication({
          applicationId,
          boatUrl,
          productType: callProductType,
          source,
          prodTesting,
          ...(imtId && { imtId }),
        });
        console.log('[processCollateralURL] success', { boatUrl, imtId, prodTesting });
      } catch (error) {
        console.warn('[processCollateralURL] lookup failed', error);
      }
    },
    []
  );

  const updateTabState = useCallback((tabName: string) => {
    if (Object.values(LendApiFullAppTab).includes(tabName as LendApiFullAppTab)) {
      const validTab = tabName as LendApiFullAppTab;
      setCurrentTab(validTab);
      setLastKnownTab(validTab);
    } else {
      setCurrentTab(tabName);
    }
  }, []);

  useEffect(() => {
    if (!funnelLoadedFired.current) {
      funnelLoadedFired.current = true;
      sendKameleoonGoalConversion(KAMELEOON_FULLAPP_FUNNEL_LOADED, 'fullApp');
    }
  }, []);

  // Listen for postMessage events from LendAPI FullApp iframe
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Only process messages from LendAPI domain
      if (event.origin !== LEND_API_ORIGIN) return;

      try {
        if (event.data?.type !== 'dimensionChange') {
          console.log(
            `[LendAPI FullApp][handleMessage] | Received event of type ${event.data?.type} for tab ${event.data?.payload?.tab_name}`,
            JSON.stringify(event.data)
          );
        }

        const eventType = event.data?.type;
        const tabName = event.data?.payload?.tab_name;
        const payload = event.data?.payload;
        const hooks = resolveProductHooks(productType, productConfig);

        // Capture the LendAPI session (used to gate the header Login button / logo
        // centering and to create the barebones loan on successful login).
        captureSessionFromPayload(payload);

        // Update tab state BEFORE async work to prevent race conditions
        // Handle pageLoad events (GA and Kameleoon tracking)
        if (event.data && eventType === 'pageLoad' && payload && tabName) {
          window.scrollTo(0, 0);
          updateTabState(tabName);
          processPerformanceTracking(tabName, productConfig, productType, payload);
        }

        // Handle formSubmission events for tab tracking
        if (event.data && eventType === 'formSubmission' && payload && tabName) {
          updateTabState(tabName);
        }

        // Process hooks (Iterable events) AFTER state updates to prevent race conditions
        await processHooks(
          event.data,
          eventType,
          tabName,
          hooks,
          productType,
          callEnrichLendAPIApplication,
          callProcessCollateralURL,
          identityRecordedRef
        );
      } catch (error) {
        console.error('[LendAPI FullApp] Error handling postMessage:', error);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // productType and productConfig are route-based and cannot change after component mounts (route changes unmount the component)
    // updateTabState, captureSessionFromPayload, callEnrichLendAPIApplication, and callProcessCollateralURL are stable (useCallback with empty deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    updateTabState,
    captureSessionFromPayload,
    callEnrichLendAPIApplication,
    callProcessCollateralURL,
  ]);

  if (!urlProductType || !productId || !productSlug) {
    const errors: string[] = [];
    if (!urlProductType) errors.push('Missing productType parameter');
    if (!productId) errors.push('Missing productId parameter');
    if (!productSlug) errors.push('Missing productSlug parameter');
    console.error(
      `[LendAPI FullApp] Missing required route parameters. Errors: ${errors.join(', ')}`
    );
    return <PageNotFound showLogin={false} />;
  }

  const validation = validateLendAPIRoute(productId, productSlug);

  if (!validation.isValid) {
    console.error(
      `[LendAPI FullApp] Invalid route parameters: productId=${productId}, productSlug=${productSlug}. Errors: ${validation.errors.join(
        ', '
      )}`
    );
    return <PageNotFound showLogin={false} />;
  }

  if ((window as any).__lendApiFullAppVersionLogged !== true) {
    console.log(`Dynamic App Version: ${DYNAMIC_APP_INTERNAL_VERSION}`);
    (window as any).__lendApiFullAppVersionLogged = true;
  }

  const sessionId = getValidSessionFromURL();
  const iframeSrc = buildLendAPIIframeSrc(productSlug, sessionId);
  console.log('[LendAPI FullApp] iframeSrc', iframeSrc);
  if (
    !isProduction() &&
    !isProdTesting(captureFullQueryString()) &&
    productConfig?.tags?.includes('prodTestingInQA')
  ) {
    console.error(
      `[LendAPI FullApp] Production funnel loaded in QA without prodTesting=true. ` +
        `Add ?prodTesting=true to the URL to use production LendAPI products in QA.`
    );
  }

  const showProgressBar = shouldShowProgressBar(currentTab, FULLAPP_PROGRESS_CONFIG);

  return (
    <PreQualFunnelLayout
      headerRight={
        <LendAPIFunnelHeaderRight hasSession={hasSession} onLoginSuccess={handleLoginSuccess} />
      }
      centerLogo={!hasSession && !isAuthenticated}
      iconTexts={LENDAPI_APPLICATION_HEADER_VALUE_PROPS}
      mobileVisibleIndex={1}
    >
      <LendAPIIframe
        src={iframeSrc}
        iframeRef={iframeRef}
        currentTab={currentTab}
        lastKnownTab={lastKnownTab}
        progressConfig={FULLAPP_PROGRESS_CONFIG}
        showProgressBar={showProgressBar}
        isFullApp
      />
    </PreQualFunnelLayout>
  );
};

export default React.memo(LendAPIFullAppFunnel);
